// Live "current value" for the portfolio-metric editor. Mirrors the alerts
// evaluator: account value is the venue's own account total from its newest
// row, and nothing at all when the venue left it off (it does so rather than
// publish an incomplete total). A currency balance's USD value comes from the
// dashboard's tickers, not the PRICE KV alerts reads, so it can differ by a tick.
import type { BalanceRow } from '@/features/dashboard/lib/balance-to-position-row'
import { safeParseFloat } from '@/lib/utils'
import type { PortfolioData } from '@/types/generated/common/account'

import type { PortfolioMetricNode } from './trigger-condition-model'

export type PortfolioMetricPreview = { native?: number; usd?: number; currency?: string }

export function portfolioMetricPreview(
  node: Pick<PortfolioMetricNode, 'metric' | 'currency'>,
  balances: BalanceRow[],
  rawRows: PortfolioData[]
): PortfolioMetricPreview | null {
  if (node.metric === 'account_value_usd') {
    const newest = rawRows.reduce<PortfolioData | undefined>(
      (best, r) => (!best || Number(r.timestamp) > Number(best.timestamp) ? r : best),
      undefined
    )
    return newest?.accountEquityUsd ? { usd: safeParseFloat(newest.accountEquityUsd) } : null
  }
  const currency = node.currency.trim().toUpperCase()
  const row = balances.find((b) => b.currency.toUpperCase() === currency)
  if (!row) return null
  return { currency, native: row.amount, usd: row.coinPrice ? row.equityValue : undefined }
}

export type PortfolioMetricGap = { kind: 'account' } | { kind: 'currencyUsd'; currency: string }

// Whether the account's portfolio has loaded yet still can't produce the value
// the threshold is in — the trigger can't fire while that holds. Null while
// nothing has loaded (no rows) so a loading form shows no warning. A currency
// the account doesn't hold is a balance of 0, not a gap.
export function portfolioMetricGap(
  node: Pick<PortfolioMetricNode, 'metric' | 'currency' | 'unit'>,
  balances: BalanceRow[],
  rawRows: PortfolioData[]
): PortfolioMetricGap | null {
  if (rawRows.length === 0) return null
  const preview = portfolioMetricPreview(node, balances, rawRows)
  if (node.metric === 'account_value_usd') return preview ? null : { kind: 'account' }
  if (node.unit !== 'usd' || !preview) return null
  return preview.usd === undefined ? { kind: 'currencyUsd', currency: preview.currency! } : null
}
